import { Api, ApiError, type Limits, type Plan, type Session } from "./api/client.ts";
import { daysRemaining, formatExpiryDate, planLabel, planProgress } from "./plan.ts";
import { Viewport } from "./render/viewport.ts";
import { AdminDialog } from "./ui/adminDialog.ts";
import { AuthDialog } from "./ui/authDialog.ts";
import { PaywallDialog, type Period } from "./ui/paywallDialog.ts";
import { ProfileDialog } from "./ui/profileDialog.ts";
import { EditPartDataDialog, type PartDataRow } from "./ui/editPartDataDialog.ts";
import { Panels } from "./ui/panels.ts";
import { QueueStrip } from "./ui/queueStrip.ts";
import type {
  CombinedExportSuccess,
  CombinedPreviewSuccess,
  ExportSuccess,
  PartSummary,
  SplitSuccess,
  UnitsSuccess,
  WorkerRequest,
  WorkerResponse,
} from "./worker/splitWorker.ts";
import SplitWorker from "./worker/splitWorker.ts?worker";

/**
 * Wiring.
 *
 * The shape of the whole product is in this file: a file goes into a worker,
 * the worker returns what it found, the user looks at it and chooses, and only
 * then does anything get written. Nothing is exported before the user has seen
 * the result — that ordering is the product, not a nicety.
 */

const api = new Api();
const worker = new SplitWorker();

/**
 * A tier — and, for Pro, a billing period — chosen on the marketing site,
 * handed off via `?plan=&period=`. Read and stripped from the URL once at
 * load — a reload must not re-trigger the register prompt or reopen the
 * paywall — and consumed the first time `promptSignIn()` runs so a later
 * sign-out/sign-in cycle isn't affected.
 */
let pendingPlan: Plan | null;
let pendingPeriod: "monthly" | "yearly";
[pendingPlan, pendingPeriod] = consumePendingPlan();

function consumePendingPlan(): [Plan | null, "monthly" | "yearly"] {
  const url = new URL(location.href);
  const value = url.searchParams.get("plan");
  const period = url.searchParams.get("period");
  if (value === null) return [null, "monthly"];

  url.searchParams.delete("plan");
  url.searchParams.delete("period");
  history.replaceState(null, "", url);
  const plan = value === "free" || value === "day" || value === "pro" ? value : null;
  return [plan, period === "yearly" ? "yearly" : "monthly"];
}

const canvas = element<HTMLCanvasElement>("canvas");
const dropzone = element("dropzone");
const dropzoneTitle = element("dropzone-title");
const dropzoneHint = element("dropzone-hint");
const dropzoneGate = element("dropzone-gate");
const gateCreateButton = element<HTMLButtonElement>("gate-create");
const gateSignInButton = element<HTMLButtonElement>("gate-signin");
const stage = document.querySelector<HTMLElement>(".stage")!;
const busy = element("busy");
const busyText = element("busy-text");
const fileInput = element<HTMLInputElement>("file");
const openButton = element<HTMLButtonElement>("open");
const browseButton = element<HTMLButtonElement>("browse");
const exportButton = element<HTMLButtonElement>("export");
const fitButton = element<HTMLButtonElement>("fit");
const avatarWrapper = element("avatar-wrapper");
const avatarButton = element<HTMLButtonElement>("avatar");
const avatarLetter = element("avatar-letter");
const avatarDropdown = element("avatar-dropdown");
const avatarPlanName = element("avatar-plan-name");
const avatarPlanExpiry = element("avatar-plan-expiry");
const avatarPlanProgress = element("avatar-plan-progress");
const avatarPlanProgressBar = element("avatar-plan-progress-bar");
const avatarPlanDaysLeft = element("avatar-plan-days-left");

/** The bar turns red once fewer than this many days remain. */
const ENDING_SOON_DAYS = 7;
const logoutButton = element<HTMLButtonElement>("logout-btn");
const adminItem = element("admin-item");
const adminButton = element<HTMLButtonElement>("admin-btn");
const profileButton = element<HTMLButtonElement>("profile-btn");
const helpLink = element<HTMLAnchorElement>("help-link");

const viewport = new Viewport(canvas);
const auth = new AuthDialog(api);
const paywall = new PaywallDialog(api);
const admin = new AdminDialog(api);
const profile = new ProfileDialog(api, paywall, (session) => void applySession(session));
const editPartData = new EditPartDataDialog();

interface State {
  result: SplitSuccess | null;
  selected: Set<number>;
  hovered: number | null;
  session: Session | null;
  layerOverrides: Map<string, string>;
  /** Layers switched off in the viewport by the user. Pure UI state, never sent to the worker. */
  hiddenLayers: Set<string>;
  /** `null` means let the engine derive the join tolerance; a number overrides it. */
  joinTolerance: number | null;
  /** Try to detect and exclude an outer border contour before nesting. Off by default. */
  removeBorder: boolean;
  /**
   * `$INSUNITS` the user picked from the Settings tab, for a file whose header
   * carried none. `null` defers to whatever the file itself says. A re-split
   * re-parses the original bytes, which would otherwise forget this choice.
   */
  unitsOverride: number | null;
  /**
   * A single entity, focused from either a precise canvas click or a click on
   * a Contents-tab text line. Distinct from `selected`/`hovered`, which are
   * part-level. Overrides the whole-part Contents highlight while set, and is
   * cleared whenever a fresh part-selection choice is made.
   */
  entityFocus: { srcStart: number; srcEnd: number } | null;
  /** The opened file's name, minus its extension. `null` until a file has been opened. */
  fileName: string | null;
  /**
   * Item name / revision / assembly entered via "Edit Part Data", keyed by
   * `PartSummary.index`. Survives a re-split (layer/tolerance/unit changes
   * regenerate `state.result.parts` but not this), and is cleared only when a
   * new file is opened.
   */
  partData: Map<number, { itemName: string; revision: string; assembly: string }>;
  /** Prepended/appended to every part's own name for export — DXF filenames and the CSV/PDF name column. Cleared only when a new file is opened. */
  namePrefix: string;
  nameSuffix: string;
}

const state: State = {
  result: null,
  selected: new Set(),
  hovered: null,
  session: null,
  layerOverrides: new Map(),
  hiddenLayers: new Set(),
  joinTolerance: null,
  removeBorder: false,
  unitsOverride: null,
  entityFocus: null,
  fileName: null,
  partData: new Map(),
  namePrefix: "",
  nameSuffix: "",
};

/** One entity's source line range and every primitive it produced. */
interface EntityIndexEntry {
  readonly srcStart: number;
  readonly srcEnd: number;
  readonly primitiveIds: number[];
}

/**
 * Every entity in the loaded document, sorted by `srcStart` for binary search
 * from a clicked Contents-tab line number. Rebuilt on each split, alongside
 * `lastFileBytes` below — derived from `state.result.display`, so it lives
 * next to `state` rather than inside it.
 */
let entityIndex: EntityIndexEntry[] = [];

const panels = new Panels(document, {
  onToggle(index, additive) {
    state.entityFocus = null;
    if (additive) {
      toggle(index);
    } else if (state.selected.size === 1 && state.selected.has(index)) {
      state.selected.clear();
    } else {
      state.selected = new Set([index]);
    }
    syncSelection();
  },
  onHover(index) {
    state.hovered = index;
    syncOverlay();
  },
  onZoomTo(index) {
    const part = state.result?.parts.find((p) => p.index === index);
    if (part) viewport.fitPart(part);
  },
  onSelectAll() {
    state.entityFocus = null;
    state.selected = new Set((state.result?.parts ?? []).map((p) => p.index));
    syncSelection();
  },
  onSelectNone() {
    state.entityFocus = null;
    state.selected.clear();
    syncSelection();
  },
  onEditPartData() {
    void openEditPartData();
  },
  onZoomToPoint(x, y) {
    const span = Math.max((state.result?.measure.width ?? 100) * 0.06, 1);
    viewport.fit({ min: { x: x - span, y: y - span, z: 0 }, max: { x: x + span, y: y + span, z: 0 } });
  },
  onLayerRoleChange(layer, role) {
    state.layerOverrides.set(layer, role);
    // Re-running detection is the honest response to a role change: chaining,
    // containment and naming all depend on which entities are cut geometry,
    // so nothing short of a re-run gives a correct answer.
    if (lastFileBytes !== null) runSplit(lastFileBytes.slice(0));
  },
  onSetUnits(code) {
    state.unitsOverride = code;
    pendingKind = "set-units";
    worker.postMessage({ kind: "set-units", fileId: activeFileId(), code });
  },
  onToleranceChange(value) {
    state.joinTolerance = value;
    if (lastFileBytes !== null) runSplit(lastFileBytes.slice(0));
  },
  onToleranceReset() {
    state.joinTolerance = null;
    if (lastFileBytes !== null) runSplit(lastFileBytes.slice(0));
  },
  onRemoveBorderChange(value) {
    state.removeBorder = value;
    if (lastFileBytes !== null) runSplit(lastFileBytes.slice(0));
  },
  onLayerVisibilityChange(layer, hidden) {
    if (hidden) state.hiddenLayers.add(layer);
    else state.hiddenLayers.delete(layer);
    viewport.setHiddenLayers(state.hiddenLayers);
    if (state.result !== null) panels.renderWarnings(visibleWarnings(state.result.warnings));
  },
  onContentsLineClick(line) {
    // A header/table/section line belongs to no entity — no-op. This does not
    // touch state.selected / export selection, matching the canvas entity
    // click's own scope.
    const entry = findEntityForLine(entityIndex, line);
    if (entry === null) return;

    state.entityFocus = { srcStart: entry.srcStart, srcEnd: entry.srcEnd };
    const bounds = viewport.boundsOfEntities(entry.primitiveIds);
    if (bounds && !viewport.isVisible(bounds)) viewport.focus(bounds);
    viewport.setHighlightedEntities(entry.primitiveIds);
    panels.setContentsHighlight([state.entityFocus], { scrollIntoView: false }, "entity");
  },
});

const queueStrip = new QueueStrip(document, {
  onSelect(index) {
    void startQueueEntry(index);
  },
  onClose(index) {
    void closeQueueEntry(index);
  },
  onNext() {
    void advanceToNextQueued();
  },
  onCombinedExport() {
    void doCombinedExport();
  },
});

/** Binary search `index` (sorted by `srcStart`) for the entry containing `line`, or null. */
function findEntityForLine(index: readonly EntityIndexEntry[], line: number): EntityIndexEntry | null {
  let lo = 0;
  let hi = index.length - 1;
  let candidate: EntityIndexEntry | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const entry = index[mid]!;
    if (entry.srcStart <= line) {
      candidate = entry;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return candidate !== null && line <= candidate.srcEnd ? candidate : null;
}

/** Warnings whose geometry lives entirely on a layer the user has hidden are noise, not signal. */
function visibleWarnings<T extends { layer?: string }>(warnings: readonly T[]): readonly T[] {
  return warnings.filter((w) => w.layer === undefined || !state.hiddenLayers.has(w.layer));
}

/**
 * The bytes of the file currently open.
 *
 * Kept so a layer-role change can re-run detection without asking the user to
 * pick the file again. It never leaves this tab.
 */
let lastFileBytes: ArrayBuffer | null = null;

/**
 * Which worker request kind is currently in flight, so the shared
 * `message` listener's failure branch can tell a bad DXF (handled inline, on
 * its own queue row) apart from a failed export or units change (still a
 * fatal, page-level error). There is only ever one request in flight, since
 * the worker is single-threaded and nothing here posts a second message
 * before the first has resolved.
 *
 * "background-*" kinds are the combined export's own silent steps — splitting
 * a file that is not the one on screen, previewing duplicates, writing the
 * merged ZIP — none of which should touch `state` the way the normal
 * `onSplit`/`onExported` handlers do. `pendingResolve`, when set, means the
 * next worker message belongs to one of those steps and is handed straight
 * back to whoever is awaiting it instead of running the side-effecting path.
 */
let pendingKind: "split" | "export" | "set-units" | "background" | null = null;
let pendingResolve: ((message: WorkerResponse) => void) | null = null;

/**
 * Set true immediately before the one `runSplit` call that represents a
 * fresh file load into the queue (as opposed to a re-split triggered by the
 * user tweaking tolerance/layer-overrides/border-removal/units on the file
 * already loaded). Snapshotted and reset by the worker message listener on
 * every message — success or failure — so a failed fresh-load split can
 * never leak `true` into the next, unrelated split.
 */
let pendingSplitIsFreshLoad = false;

/** Send a request and resolve once the worker answers, without touching `state`. */
function request(message: WorkerRequest, transfer: Transferable[] = []): Promise<WorkerResponse> {
  return new Promise((resolve) => {
    pendingKind = "background";
    pendingResolve = resolve;
    worker.postMessage(message, transfer);
  });
}

/**
 * The queue entry currently on screen, as the id every worker request against
 * it is keyed by. Every action that can reach the worker already requires
 * `state.result !== null`, which is only ever set once a queue entry has
 * been made active, so this is safe wherever those actions call it.
 */
function activeFileId(): string {
  return String(queue[activeQueueIndex]!.id);
}

function mayCombineExport(): boolean {
  return state.session?.limits?.mayDeduplicateExport === true;
}

/** One file selected or dropped, and where it stands in the queue. */
interface QueueEntry {
  readonly id: number;
  readonly file: File;
  status: "queued" | "processing" | "review" | "exported" | "error";
  partCount: number | null;
  /** Whether this file's own parts contain a same-shape duplicate, once split. */
  hasDuplicateParts: boolean;
  errorMessage: string | null;
  /** This entry's edits, captured when navigating away from it. Absent until the first time it's left. */
  savedEdits?: {
    layerOverrides: Map<string, string>;
    hiddenLayers: Set<string>;
    joinTolerance: number | null;
    removeBorder: boolean;
    unitsOverride: number | null;
    partData: Map<number, { itemName: string; revision: string; assembly: string }>;
    namePrefix: string;
    nameSuffix: string;
  };
}

/**
 * Every file selected/dropped this session, in the order added. A lone file
 * is still a one-entry queue — the strip itself only becomes visible once a
 * second file is queued (see `QueueStrip.render`), so opening a single DXF
 * looks exactly as it always has.
 */
const queue: QueueEntry[] = [];
/** Index into `queue` of the file currently on screen; -1 before anything starts. */
let activeQueueIndex = -1;
/** The queue entry `state` currently reflects, tracked by reference so a save-out doesn't depend on index arithmetic a caller (e.g. `closeQueueEntry`) may have already changed. */
let activeEntry: QueueEntry | null = null;
let nextQueueId = 1;

function renderQueue(): void {
  queueStrip.render(
    queue.map((entry) => ({
      name: entry.file.name,
      status: entry.status,
      partCount: entry.partCount,
      hasDuplicateParts: entry.hasDuplicateParts,
      errorMessage: entry.errorMessage,
    })),
    activeQueueIndex,
  );
}

// --- file input ------------------------------------------------------------

openButton.addEventListener("click", () => fileInput.click());
browseButton.addEventListener("click", () => fileInput.click());

// The gate panels are the dropzone's own content while signed out — see
// renderGate() — so they're the normal, always-visible way in; only a
// drag-and-drop bypasses them straight to enqueueFiles()'s ensureSignedIn().
gateCreateButton.addEventListener("click", (event) => {
  event.stopPropagation();
  void startCreateAccount();
});
gateSignInButton.addEventListener("click", (event) => {
  event.stopPropagation();
  void startSignIn();
});
fileInput.addEventListener("change", () => {
  if (fileInput.files) void enqueueFiles([...fileInput.files]);
  fileInput.value = "";
});

for (const event of ["dragenter", "dragover"] as const) {
  stage.addEventListener(event, (e) => {
    e.preventDefault();
    stage.classList.add("is-dragging");
    dropzone.classList.add("is-dragging");
  });
}
for (const event of ["dragleave", "drop"] as const) {
  stage.addEventListener(event, (e) => {
    e.preventDefault();
    stage.classList.remove("is-dragging");
    dropzone.classList.remove("is-dragging");
  });
}
stage.addEventListener("drop", (e) => {
  if (e.dataTransfer?.files) void enqueueFiles([...e.dataTransfer.files]);
});

/**
 * Add one or more files to the queue. The first newly added file always
 * takes over the main area immediately — opening a file is not something
 * that should silently queue behind whatever is already on screen. If the
 * file currently on screen has already been exported it's done with, so it
 * gets dropped from the queue entirely rather than left behind; otherwise
 * it simply falls back into the queue, still there to return to.
 */
async function enqueueFiles(files: readonly File[]): Promise<void> {
  const dxfFiles = files.filter((f) => /\.dxf$/i.test(f.name));
  if (dxfFiles.length === 0) return;
  if (!(await ensureSignedIn())) return;

  if (activeQueueIndex !== -1 && queue[activeQueueIndex]!.status === "exported") {
    queue.splice(activeQueueIndex, 1);
    activeQueueIndex = -1;
  }

  const firstNewIndex = queue.length;
  for (const file of dxfFiles) {
    queue.push({
      id: nextQueueId++,
      file,
      status: "queued",
      partCount: null,
      hasDuplicateParts: false,
      errorMessage: null,
    });
  }
  renderQueue();
  await startQueueEntry(firstNewIndex);
}

/** Start the next not-yet-started file after the currently active one, if any. */
async function advanceToNextQueued(): Promise<void> {
  const next = queue.findIndex((entry, i) => i > activeQueueIndex && entry.status === "queued");
  if (next !== -1) await startQueueEntry(next);
}

/**
 * Load and split the queue entry at `index`, making it the file on screen —
 * whether that entry is still queued, already reviewed, exported, or even
 * errored. Going back to an earlier file re-splits it from its original
 * bytes, the same as opening it fresh; whatever was on screen before this
 * call is snapshotted into its own `savedEdits` first, and the entry being
 * switched to has its own snapshot (if any) restored — so layer/tolerance/
 * border/units choices and "Edit Part Data" values round-trip through a
 * detour to another queued nest.
 *
 * Ignored while another load/split/export is already in flight (`!busy.hidden`):
 * two split requests in flight at once would race, since the worker's
 * response carries nothing to say which request it answers.
 */
async function startQueueEntry(index: number): Promise<void> {
  if (!busy.hidden) return;

  if (activeEntry !== null) {
    activeEntry.savedEdits = {
      layerOverrides: new Map(state.layerOverrides),
      hiddenLayers: new Set(state.hiddenLayers),
      joinTolerance: state.joinTolerance,
      removeBorder: state.removeBorder,
      unitsOverride: state.unitsOverride,
      partData: new Map(state.partData),
      namePrefix: state.namePrefix,
      nameSuffix: state.nameSuffix,
    };
  }

  const entry = queue[index]!;
  activeQueueIndex = index;
  entry.status = "processing";
  entry.errorMessage = null;
  renderQueue();

  setBusy(`Reading ${entry.file.name}…`);
  const bytes = await entry.file.arrayBuffer();
  lastFileBytes = bytes.slice(0);
  const saved = entry.savedEdits;
  state.layerOverrides.clear();
  if (saved) for (const [layer, role] of saved.layerOverrides) state.layerOverrides.set(layer, role);
  state.hiddenLayers.clear();
  if (saved) for (const layer of saved.hiddenLayers) state.hiddenLayers.add(layer);
  state.joinTolerance = saved?.joinTolerance ?? null;
  state.removeBorder = saved?.removeBorder ?? false;
  state.unitsOverride = saved?.unitsOverride ?? null;
  state.fileName = entry.file.name.replace(/\.dxf$/i, "");
  state.partData.clear();
  if (saved) for (const [partIndex, data] of saved.partData) state.partData.set(partIndex, data);
  state.namePrefix = saved?.namePrefix ?? "";
  state.nameSuffix = saved?.nameSuffix ?? "";
  panels.renderRemoveBorder(state.removeBorder);
  activeEntry = entry;
  pendingSplitIsFreshLoad = true;
  runSplit(bytes);
}

/**
 * Drop the queue entry at `index`. If it was the file on screen, the next
 * remaining entry (or the previous one, if it was last) takes its place;
 * if it was the only entry, the app reverts to the empty drag-and-drop
 * screen. Ignored while a load/split/export is in flight, same as
 * `startQueueEntry`.
 */
async function closeQueueEntry(index: number): Promise<void> {
  if (!busy.hidden) return;

  queue.splice(index, 1);

  if (queue.length === 0) {
    activeQueueIndex = -1;
    resetToEmpty();
    renderQueue();
    return;
  }

  if (index < activeQueueIndex) {
    activeQueueIndex -= 1;
    renderQueue();
  } else if (index === activeQueueIndex) {
    await startQueueEntry(Math.min(index, queue.length - 1));
  } else {
    renderQueue();
  }
}

/** Return to the pre-open state: empty canvas, hidden panel, visible dropzone. */
function resetToEmpty(): void {
  activeEntry = null;
  state.result = null;
  state.selected = new Set();
  state.hovered = null;
  state.entityFocus = null;
  state.fileName = null;
  state.partData.clear();
  state.namePrefix = "";
  state.nameSuffix = "";
  state.layerOverrides.clear();
  state.hiddenLayers.clear();
  state.joinTolerance = null;
  state.removeBorder = false;
  state.unitsOverride = null;

  lastFileBytes = null;
  entityIndex = [];

  panels.hide();
  dropzone.hidden = false;
  fitButton.disabled = true;
  exportButton.disabled = true;
  viewport.clear();
}

/** Open the "Edit Part Data" grid and save back whatever the user submits. */
async function openEditPartData(): Promise<void> {
  const parts = state.result?.parts ?? [];
  if (parts.length === 0) return;

  const rows: PartDataRow[] = parts.map((part) => {
    const existing = state.partData.get(part.index);
    return {
      index: part.index,
      partName: part.name,
      itemName: existing?.itemName ?? part.name,
      revision: existing?.revision ?? "",
      assembly: existing?.assembly ?? state.fileName ?? "",
    };
  });

  const result = await editPartData.open(rows, state.namePrefix, state.nameSuffix);
  if (result === null) return;

  for (const row of result.rows) {
    state.partData.set(row.index, { itemName: row.itemName, revision: row.revision, assembly: row.assembly });
  }
  state.namePrefix = result.namePrefix;
  state.nameSuffix = result.nameSuffix;
}

function runSplit(bytes: ArrayBuffer): void {
  setBusy("Finding parts…");
  pendingKind = "split";
  const overrides = Object.fromEntries(state.layerOverrides) as Record<
    string,
    "cut" | "mark" | "annotation" | "ignore"
  >;
  worker.postMessage(
    {
      kind: "split",
      fileId: activeFileId(),
      bytes,
      options: {
        ...(state.layerOverrides.size > 0 ? { layerOverrides: overrides } : {}),
        ...(state.joinTolerance !== null ? { joinTolerance: state.joinTolerance } : {}),
        ...(state.removeBorder ? { removeBorder: true } : {}),
      },
    },
    // Transferred, so a 6MB nest is not copied into the worker.
    [bytes],
  );
}

// --- worker ----------------------------------------------------------------

worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
  const message = event.data;
  const kind = pendingKind;
  pendingKind = null;
  const wasFreshLoad = pendingSplitIsFreshLoad;
  pendingSplitIsFreshLoad = false;

  if (pendingResolve !== null) {
    const resolve = pendingResolve;
    pendingResolve = null;
    resolve(message);
    return;
  }

  if (!message.ok) {
    clearBusy();

    // A bad file mid-batch is that file's problem, not a page-level failure:
    // mark its row and let the user move past it with "Next file". A file
    // opened alone (queue.length === 1) falls through to the fatal path
    // below, unchanged from before the queue existed.
    if (kind === "split" && activeQueueIndex !== -1 && queue.length > 1) {
      const entry = queue[activeQueueIndex]!;
      entry.status = "error";
      entry.errorMessage = message.message;
      state.result = null;
      exportButton.disabled = true;
      renderQueue();
      return;
    }

    showFatal(message.message);
    return;
  }

  if ("kind" in message && message.kind === "export") {
    onExported(message);
    return;
  }

  if ("kind" in message && message.kind === "units") {
    onUnitsSet(message);
    return;
  }

  onSplit(message as SplitSuccess, wasFreshLoad);
});

function onSplit(result: SplitSuccess, isFreshLoad: boolean): void {
  clearBusy();

  if (isFreshLoad) {
    // Usage bookkeeping only — never let a failure here surface to the user
    // or block anything; export/split are already resolved by this point.
    void api.recordSplit(result.parts.length).catch(() => undefined);
  }

  state.result = result;
  state.selected = new Set(result.parts.map((p) => p.index));
  state.hovered = null;
  state.entityFocus = null;

  if (activeQueueIndex !== -1) {
    const entry = queue[activeQueueIndex]!;
    entry.status = "review";
    entry.partCount = result.parts.length;
    entry.hasDuplicateParts = result.hasDuplicateParts;
    renderQueue();
  }

  dropzone.hidden = true;
  panels.show();
  fitButton.disabled = false;

  viewport.load(result.display, result.layerRecords as never);
  viewport.resized();
  viewport.setHiddenLayers(state.hiddenLayers);
  entityIndex = buildEntityIndex(result.display.primitives);

  panels.renderParts(result.parts);
  panels.renderLayers(result.layers, mayRemapLayers(), state.layerOverrides, state.hiddenLayers);
  panels.renderTolerance(result.tolerances.join, state.joinTolerance);
  panels.renderContents(result.source);
  syncSelection();

  exportButton.disabled = result.parts.length === 0;

  // A re-split re-parses the original bytes, which forgets any units choice
  // made for a file whose header carried none — reapply it rather than
  // showing the "no-units" warning again for a file the user already fixed.
  if (state.unitsOverride !== null) {
    pendingKind = "set-units";
    worker.postMessage({ kind: "set-units", fileId: activeFileId(), code: state.unitsOverride });
  } else {
    panels.renderUnits(result.units);
    panels.renderWarnings(visibleWarnings(result.warnings));
  }
}

/**
 * Group the display list's primitives by the entity that produced them
 * (`ref.srcStart` is unique per entity in valid DXF, since entities are
 * non-overlapping sequential records), sorted for binary search by line.
 */
function buildEntityIndex(primitives: SplitSuccess["display"]["primitives"]): EntityIndexEntry[] {
  const bySrcStart = new Map<number, EntityIndexEntry>();
  primitives.forEach((primitive, id) => {
    const ref = primitive.ref;
    let entry = bySrcStart.get(ref.srcStart);
    if (entry === undefined) {
      entry = { srcStart: ref.srcStart, srcEnd: ref.srcEnd, primitiveIds: [] };
      bySrcStart.set(ref.srcStart, entry);
    }
    entry.primitiveIds.push(id);
  });
  return Array.from(bySrcStart.values()).sort((a, b) => a.srcStart - b.srcStart);
}

function onUnitsSet(message: UnitsSuccess): void {
  if (state.result === null) return;
  state.result = { ...state.result, units: message.units, warnings: message.warnings };
  panels.renderUnits(message.units);
  panels.renderWarnings(visibleWarnings(message.warnings));
}

function onExported(message: ExportSuccess): void {
  clearBusy();

  if (activeQueueIndex !== -1) {
    queue[activeQueueIndex]!.status = "exported";
    renderQueue();
  }

  downloadZip(message.zip, message.filename);

  if (message.omitted > 0) {
    panels.renderWarnings([
      {
        code: "truncated",
        severity: "warning",
        message:
          `Wrote ${message.written} parts. The remaining ${message.omitted} were not ` +
          `included, because the free tier writes the first ${message.written}.`,
      },
      ...visibleWarnings(state.result?.warnings ?? []),
    ]);
  }
}

// --- selection -------------------------------------------------------------

function toggle(index: number): void {
  if (state.selected.has(index)) state.selected.delete(index);
  else state.selected.add(index);
}

function syncSelection(): void {
  panels.renderSelection(state.selected);
  syncOverlay();
  syncEntityHighlight();
  exportButton.disabled = state.selected.size === 0;
}

function syncOverlay(): void {
  viewport.setOverlay({
    parts: (state.result?.parts ?? []) as readonly PartSummary[],
    selected: state.selected,
    hovered: state.hovered,
    openContours: state.result?.openContours ?? [],
  });
}

/**
 * Keep the Contents tab (and, for a single-entity focus, the canvas highlight)
 * in step with the current selection. A single-entity focus always wins over
 * a whole-part selection; whole-part selection highlights the Contents tab
 * only — the canvas already shows it via the part outline/fill in
 * `drawOverlay()`, so stroking every one of its primitives too would be
 * redundant.
 */
function syncEntityHighlight(): void {
  if (state.entityFocus !== null) {
    const focus = state.entityFocus;
    const entry = entityIndex.find((e) => e.srcStart === focus.srcStart && e.srcEnd === focus.srcEnd);
    viewport.setHighlightedEntities(entry ? entry.primitiveIds : []);
    // Unlike the whole-part case below, this always comes from a fresh canvas
    // click (the user's eyes were on the canvas, not the Contents tab), so the
    // highlighted line needs to be scrolled into view or it's invisible.
    panels.setContentsHighlight([focus], { scrollIntoView: true }, "entity");
    return;
  }

  if (state.selected.size > 0) {
    const spans = (state.result?.parts ?? [])
      .filter((p) => state.selected.has(p.index))
      .flatMap((p) => p.entitySpans);
    viewport.setHighlightedEntities([]);
    panels.setContentsHighlight(spans, { scrollIntoView: true }, "part");
    return;
  }

  viewport.setHighlightedEntities([]);
  panels.setContentsHighlight([], { scrollIntoView: false }, "part");
}

// --- canvas interaction ----------------------------------------------------

let panning = false;
let panFrom = { x: 0, y: 0 };
let movedWhilePanning = false;

canvas.addEventListener("pointerdown", (event) => {
  panning = true;
  movedWhilePanning = false;
  panFrom = { x: event.clientX, y: event.clientY };
  canvas.setPointerCapture(event.pointerId);
  canvas.classList.add("is-panning");
});

canvas.addEventListener("pointermove", (event) => {
  if (panning) {
    const dx = event.clientX - panFrom.x;
    const dy = event.clientY - panFrom.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedWhilePanning = true;
    viewport.pan(dx, dy);
    panFrom = { x: event.clientX, y: event.clientY };
    viewport.setHoveredEntity(null, null);
    return;
  }

  const screen = pointIn(event);

  const hit = viewport.partAt(screen);
  canvas.classList.toggle("is-over-part", hit !== null);
  if (hit !== state.hovered) {
    state.hovered = hit;
    syncOverlay();
  }

  viewport.setHoveredEntity(viewport.pickEntity(screen), screen);
});

canvas.addEventListener("pointerleave", () => {
  viewport.setHoveredEntity(null, null);
});

canvas.addEventListener("pointerup", (event) => {
  panning = false;
  canvas.classList.remove("is-panning");
  canvas.releasePointerCapture(event.pointerId);

  // A drag that moved the view is not a click on a part.
  if (movedWhilePanning) return;

  const screen = pointIn(event);

  // A precise click on an entity's own geometry (as opposed to a part's fill)
  // only narrows the Contents tab highlight — it never touches export
  // selection, so it's handled here and returns before the part-fill logic.
  const entityHit = viewport.pickEntity(screen);
  if (entityHit !== null) {
    const ref = entityHit.entry.primitive.ref;
    state.entityFocus = { srcStart: ref.srcStart, srcEnd: ref.srcEnd };
    syncEntityHighlight();
    return;
  }

  const hit = viewport.partAt(screen);
  if (hit === null) return;

  state.entityFocus = null;
  if (event.shiftKey || event.metaKey || event.ctrlKey) toggle(hit);
  else if (state.selected.size === 1 && state.selected.has(hit)) state.selected.clear();
  else state.selected = new Set([hit]);

  syncSelection();
});

canvas.addEventListener(
  "wheel",
  (event) => {
    event.preventDefault();
    // A trackpad reports many small deltas and a mouse a few large ones;
    // exponentiating keeps both feeling the same.
    viewport.zoomAt(pointIn(event), Math.exp(-event.deltaY * 0.0015));
  },
  { passive: false },
);

fitButton.addEventListener("click", () => viewport.fitAll());

function pointIn(event: MouseEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

new ResizeObserver(() => viewport.resized()).observe(stage);

// --- export ----------------------------------------------------------------

exportButton.addEventListener("click", () => void doExport());

async function doExport(): Promise<void> {
  if (state.result === null || state.selected.size === 0) return;

  // Refused up front, from the entitlement the session already carries, so a
  // Free account that has used its one nest or an expired Day pass/Pro sees
  // the paywall immediately rather than after a round trip that can only say
  // no. `undefined` (limits not loaded yet) is not a refusal — let the
  // request below settle that.
  if (state.session?.limits?.mayExport === false) {
    const redeemed = await paywall.open(state.session);
    if (redeemed !== null) {
      await applySession(redeemed);
      await doExport();
    }
    return;
  }

  setBusy("Checking your plan…");

  let grant;
  try {
    // The count sent is what detection found, not what is selected: the server
    // uses it only to warn about truncation, and it should describe the file.
    grant = await api.entitlement(state.result.parts.length);
  } catch (error) {
    clearBusy();

    if (error instanceof ApiError && error.status === 401 && error.code === "session_superseded") {
      // Already handled by handleSessionSuperseded() (wired to
      // Api.onSessionSuperseded, fired before this catch ever runs): it
      // clears state and shows its own "signed in elsewhere" prompt. Nothing
      // left to do here — showing "Sign in to export this nest." on top
      // would be a second, misleading dialog for the same event.
      return;
    }

    if (error instanceof ApiError && error.status === 401) {
      // Not signed in is not an error to report, it is a step to offer. The
      // detection results stay on screen behind the dialog, so a dismissed
      // prompt leaves the user exactly where they were.
      const session = await signInOrSignUpOrCancel("Sign in to export this nest.");
      if (session === null || session === "pending-verification") return;
      state.session = session;
      renderAccount(session);
      renderGate();
      if (session.limits) await loadPro(session.limits);
      panels.renderLayers(state.result.layers, mayRemapLayers(), state.layerOverrides, state.hiddenLayers);
      await doExport();
      return;
    }

    if (error instanceof ApiError && error.status === 402) {
      // The session's own `limits` said this would work, but the server's
      // fresher view disagrees — stale state, not a fluke worth suppressing.
      const redeemed = await paywall.open(state.session);
      if (redeemed !== null) {
        await applySession(redeemed);
        await doExport();
      }
      return;
    }

    if (error instanceof ApiError) {
      showFatal(error.message);
      return;
    }
    showFatal("Could not reach the server. Check your connection and try again.");
    return;
  }

  if (grant.notice !== null) {
    panels.renderWarnings([
      { code: "truncated", severity: "warning", message: grant.notice },
      ...visibleWarnings(state.result.warnings),
    ]);
  }

  setBusy("Writing parts…");
  pendingKind = "export";
  worker.postMessage({
    kind: "export",
    fileId: activeFileId(),
    token: grant.token,
    partIndices: [...state.selected],
    maxParts: grant.limits.maxParts,
    translateToOrigin: true,
    fileName: state.fileName,
    partData: [...state.partData].map(([index, data]) => ({ index, ...data })),
    namePrefix: state.namePrefix,
    nameSuffix: state.nameSuffix,
  });
}

/**
 * Export every queued file together, one instance of each distinct part
 * shape. Unlike `doExport()`, this never touches `state` — it walks the
 * queue itself, re-splitting whatever is not already on screen with plain
 * default options (the same "no memory across queue entries" rule
 * `startQueueEntry`'s own doc comment already states), then asks the worker
 * to compare and write them as one ZIP.
 */
async function doCombinedExport(): Promise<void> {
  if (!busy.hidden || queue.length < 1) return;

  setBusy("Preparing files…");
  for (const entry of queue) {
    if (entry.status === "error") continue;
    const bytes = await entry.file.arrayBuffer();
    const response = await request(
      { kind: "split", fileId: String(entry.id), bytes, options: {} },
      [bytes],
    );
    if (!response.ok) {
      entry.status = "error";
      entry.errorMessage = response.message;
      continue;
    }
    const success = response as SplitSuccess;
    entry.partCount = success.parts.length;
    entry.hasDuplicateParts = success.hasDuplicateParts;
    // A background split of the file currently on screen must not leave the
    // canvas/panels out of step with what the worker now holds for it.
    if (queue[activeQueueIndex] === entry) {
      onSplit(success, false);
      // onSplit() clears the busy overlay for the file it just brought on
      // screen — put it back while the remaining queued files still split.
      setBusy("Preparing files…");
    } else {
      entry.status = entry.status === "queued" ? "review" : entry.status;
    }
  }
  renderQueue();

  const usable = queue.filter((e) => e.status !== "error");
  if (usable.length < 1) {
    clearBusy();
    showFatal("At least one file needs to split successfully before a combined export can run.");
    return;
  }

  setBusy("Comparing parts…");
  const files = usable.map((e) => ({ fileId: String(e.id), partIndices: [] as number[] }));
  const previewResponse = await request({ kind: "combined-preview", files });
  if (!previewResponse.ok) {
    clearBusy();
    showFatal(previewResponse.message);
    return;
  }
  const preview = previewResponse as CombinedPreviewSuccess;

  clearBusy();
  const duplicateNote =
    preview.duplicates.length > 0
      ? ` ${preview.duplicates.length} duplicate ${preview.duplicates.length === 1 ? "part" : "parts"} will be skipped.`
      : " No duplicates were found.";
  const proceed = confirm(
    `${preview.uniqueParts} unique ${preview.uniqueParts === 1 ? "part" : "parts"} across ${usable.length} files.` +
      duplicateNote +
      ` Export ${preview.uniqueParts}?`,
  );
  if (!proceed) return;

  if (state.session?.limits?.mayExport === false) {
    const redeemed = await paywall.open(state.session);
    if (redeemed !== null) await applySession(redeemed);
    return;
  }

  // Distinct from the mayExport refusal above: this account can export fine,
  // it just doesn't have the combined/deduplicated feature — Day pass and
  // Free both land here, since only Pro carries mayDeduplicateExport.
  if (!mayCombineExport()) {
    const redeemed = await paywall.open(
      state.session,
      "Splitting and de-duping automatically of multiple files requires a monthly or annual plan.",
    );
    if (redeemed !== null) await applySession(redeemed);
    return;
  }

  setBusy("Checking your plan…");
  let grant;
  try {
    grant = await api.entitlement(preview.uniqueParts, true);
  } catch (error) {
    clearBusy();
    if (error instanceof ApiError && error.status === 401 && error.code === "session_superseded") {
      // See the equivalent branch in doExport() — handleSessionSuperseded()
      // already showed its own prompt.
      return;
    }
    if (error instanceof ApiError && error.status === 401) {
      const session = await signInOrSignUpOrCancel("Sign in to export this nest.");
      if (session === null || session === "pending-verification") return;
      await applySession(session);
      await doCombinedExport();
      return;
    }
    if (error instanceof ApiError && error.status === 402) {
      // The session's own `limits` said this would work, but the server's
      // fresher view disagrees (e.g. a Day pass that expired between page
      // load and this click) — its message is specific to what it refused,
      // so show that rather than the generic reason.
      const redeemed = await paywall.open(state.session, error.message);
      if (redeemed !== null) await applySession(redeemed);
      return;
    }
    showFatal(error instanceof ApiError ? error.message : "Could not reach the server. Check your connection and try again.");
    return;
  }

  setBusy("Writing parts…");
  const exportResponse = await request({
    kind: "combined-export",
    token: grant.token,
    maxParts: grant.limits.maxParts,
    translateToOrigin: true,
    files: usable.map((e) => ({
      fileId: String(e.id),
      fileName: e.file.name.replace(/\.dxf$/i, ""),
      partIndices: [],
      partData: [],
    })),
  });
  clearBusy();

  if (!exportResponse.ok) {
    showFatal(exportResponse.message);
    return;
  }
  const result = exportResponse as CombinedExportSuccess;

  for (const entry of usable) entry.status = "exported";
  renderQueue();
  downloadZip(result.zip, result.filename);

  const notes: string[] = [`Wrote ${result.written} unique parts from ${usable.length} files.`];
  if (result.collapsed > 0) notes.push(`${result.collapsed} duplicate parts were collapsed.`);
  if (result.omitted > 0) notes.push(`${result.omitted} more were left out by your plan's part cap.`);
  panels.renderWarnings([{ code: "truncated", severity: "info", message: notes.join(" ") }]);
}

/** Trigger a browser download of an in-memory ZIP, revoked shortly after the click fires. */
function downloadZip(zip: ArrayBuffer, filename: string): void {
  const blob = new Blob([zip], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --- session ---------------------------------------------------------------

function mayRemapLayers(): boolean {
  return state.session?.limits?.mayRemapLayers === true;
}

/**
 * Adopt a freshly fetched session after something outside the normal load
 * changed it — currently just a redeemed unlock code. Mirrors what
 * promptSignIn() and doExport()'s 401 branch already do by hand.
 */
async function applySession(session: Session): Promise<void> {
  state.session = session;
  renderAccount(session);
  renderGate();
  if (session.limits) await loadPro(session.limits);
  if (state.result !== null) {
    panels.renderLayers(state.result.layers, mayRemapLayers(), state.layerOverrides, state.hiddenLayers);
  }
  renderQueue();
}

async function loadSession(): Promise<void> {
  try {
    const session = await api.session();
    state.session = session;
    renderAccount(session);
    renderGate();
    if (session.signedIn && session.limits) await loadPro(session.limits);
    else await promptSignIn();
    renderQueue();
  } catch {
    // A server that is down should not stop someone who is already signed in
    // from continuing to work with what loaded; but an anonymous visitor still
    // can't be verified, so the gate stays up.
  }
}

/** Show the avatar, with its dropdown, only while an account is signed in. */
function renderAccount(session: Session): void {
  if (!session.signedIn) {
    avatarWrapper.hidden = true;
    avatarDropdown.hidden = true;
    adminItem.hidden = true;
    return;
  }
  const email = session.email ?? "";
  const firstName = session.firstName ?? "";
  avatarWrapper.hidden = false;
  avatarLetter.textContent = (firstName || email).charAt(0).toUpperCase() || "?";
  avatarButton.title = email;

  avatarPlanName.textContent = `Plan: ${planLabel(session.plan)}`;
  const showsExpiry =
    (session.plan === "day" || session.plan === "pro") && session.planExpiresAt != null;
  if (showsExpiry) {
    avatarPlanExpiry.textContent = formatExpiryDate(session.planExpiresAt!);
  }
  avatarPlanExpiry.hidden = !showsExpiry;

  const progress = showsExpiry ? planProgress(session.planStartedAt, session.planExpiresAt!) : null;
  if (progress !== null) {
    const percent = Math.round(progress * 100);
    avatarPlanProgressBar.style.width = `${percent}%`;
    avatarPlanProgress.setAttribute("aria-valuenow", String(percent));
  }
  avatarPlanProgress.hidden = progress === null;

  const daysLeft = showsExpiry ? daysRemaining(session.planExpiresAt!) : null;
  const showsDaysLeft = daysLeft !== null && daysLeft > 0;
  if (showsDaysLeft) {
    avatarPlanDaysLeft.textContent = daysLeft === 1 ? "1 day left" : `${daysLeft} days left`;
  }
  avatarPlanDaysLeft.hidden = !showsDaysLeft;

  avatarPlanProgressBar.classList.toggle(
    "avatar-plan-progress__bar--ending-soon",
    daysLeft !== null && daysLeft > 0 && daysLeft < ENDING_SOON_DAYS,
  );

  adminItem.hidden = session.isAdmin !== true;
}

/**
 * Register and, for a paid plan chosen on the marketing site, take payment
 * — the one case that still opens a dialog unprompted, since the tier (and,
 * for Pro, the billing period) was already chosen before arriving here.
 * Everyone else signed out sees the gate built into the dropzone itself
 * (see `renderGate()`) and picks "Create an account" or "Sign in" there.
 */
async function promptSignIn(): Promise<void> {
  const plan = pendingPlan;
  const period = pendingPeriod;
  pendingPlan = null;
  if (plan === null) return;

  const session = await registerForPlan(plan, period);
  // The dialog itself already showed "check your email" — there is no
  // session yet to apply, and the dropzone gate stays up until it verifies.
  if (session === "pending-verification") return;
  await applySession(session);
}

/**
 * Open straight to the login form for the dropzone gate's "Sign in" panel.
 * Never dismissable — there is nothing to do here signed out — so this
 * always resolves with a session.
 */
async function startSignIn(): Promise<void> {
  const session = await auth.open("Sign in to start splitting.", { mode: "login", dismissable: false });
  if (session === null) throw new Error("The sign-in dialog closed without a session.");
  await applySession(session);
}

/**
 * Send the dropzone gate's "Create an account" panel through the plan
 * picker (Free included, since there's no account yet to attach a later
 * upgrade to) and then registration. Backing out of the picker just leaves
 * the gate showing — there's nothing to fall back to otherwise, but nothing
 * forces the choice either.
 */
async function startCreateAccount(): Promise<void> {
  const choice = await paywall.openPlanPicker();
  if (choice === null) return;
  const session = await registerForPlan(choice.plan, choice.period);
  if (session === "pending-verification") return;
  await applySession(session);
}

/**
 * Drive the account-required gate to a signed-in session via the dialog's
 * own sign-in/create-account choice — used only where a drag-and-drop
 * bypasses the dropzone gate's panels entirely (`ensureSignedIn()`). Never
 * dismissable, so this always resolves with a session.
 */
async function signInOrSignUp(message: string): Promise<Session | "pending-verification"> {
  for (;;) {
    const result = await auth.open(message, { dismissable: false });
    if (result === "signup") {
      const choice = await paywall.openPlanPicker();
      if (choice === null) continue; // back to the sign-in/create-account choice
      return await registerForPlan(choice.plan, choice.period);
    }
    // A null result never happens in practice — the dialog above isn't
    // dismissable — but loop defensively so this stays correct if that ever
    // changes, rather than claiming a session that doesn't exist.
    if (result !== null) return result;
  }
}

/**
 * Like `signInOrSignUp`, but dismissable — cancelling the sign-in dialog, or
 * backing out of the plan picker after choosing "Create an account",
 * resolves `null` instead of looping back. Used only by the export-time
 * re-auth prompt, where the session can expire after a file was already
 * loaded (no account gate to have caught it there) and a dismissed prompt
 * should leave the user exactly where they were, not force a sign-in.
 */
async function signInOrSignUpOrCancel(message: string): Promise<Session | "pending-verification" | null> {
  const result = await auth.open(message);
  if (result === null) return null;
  if (result === "signup") {
    const choice = await paywall.openPlanPicker();
    if (choice === null) return null;
    return await registerForPlan(choice.plan, choice.period);
  }
  return result;
}

/**
 * Register, then for a paid plan continue straight to payment/voucher entry.
 *
 * Returns `"pending-verification"` instead of a `Session` when registration
 * succeeded but the account cannot sign in yet — the dialog itself already
 * showed "check your email" for this case. There is deliberately no
 * deferred-checkout path here: even a paid-plan signup stops at "check your
 * email" and pays/redeems after verifying and signing in normally, so the
 * server-side rule "no session before verification" has no exception.
 */
async function registerForPlan(plan: Plan, period: Period): Promise<Session | "pending-verification"> {
  const session = await auth.open(`Create your account to start on the ${planLabel(plan)} plan.`, {
    mode: "register",
    dismissable: false,
  });
  if (session === null) throw new Error("The registration dialog closed without a session.");
  if (session === "pending-verification") return session;

  if (plan === "day" || plan === "pro") {
    const redeemed = await paywall.openForPlan(session, plan, period);
    if (redeemed !== null) return redeemed;
  }
  return session;
}

/**
 * An account is required to split a nest at all — not just to export — so
 * that the free tier is one nest per account rather than an unlimited one
 * per visit. This reflects that in the dropzone before anyone tries to open
 * a file; `enqueueFiles()` is the actual enforcement, since a drag-and-drop
 * bypasses these buttons entirely.
 */
function renderGate(): void {
  const signedIn = state.session?.signedIn === true;
  openButton.disabled = !signedIn;
  browseButton.disabled = !signedIn;
  dropzoneTitle.hidden = !signedIn;
  dropzoneHint.hidden = !signedIn;
  dropzoneGate.hidden = signedIn;
}

avatarButton.addEventListener("click", (event) => {
  event.stopPropagation();
  avatarDropdown.hidden = !avatarDropdown.hidden;
});
document.addEventListener("click", (event) => {
  if (!avatarDropdown.hidden && !avatarWrapper.contains(event.target as Node)) {
    avatarDropdown.hidden = true;
  }
});
logoutButton.addEventListener("click", () => void onLogout());
adminButton.addEventListener("click", () => {
  avatarDropdown.hidden = true;
  void admin.open();
});
profileButton.addEventListener("click", () => {
  avatarDropdown.hidden = true;
  if (state.session) void profile.open(state.session);
});
helpLink.addEventListener("click", () => {
  avatarDropdown.hidden = true;
});

async function onLogout(): Promise<void> {
  avatarDropdown.hidden = true;
  await api.logout().catch(() => undefined);
  state.session = { signedIn: false };
  renderAccount(state.session);
  resetToEmpty();
  queue.length = 0;
  activeQueueIndex = -1;
  renderGate();
  renderQueue();
  await promptSignIn();
}

/**
 * Used at the top of any action that requires an account (opening a file,
 * exporting). Returns true once `state.session` is signed in, prompting for
 * sign-in first if it wasn't already.
 */
async function ensureSignedIn(message = "Sign in to start splitting."): Promise<boolean> {
  if (state.session?.signedIn === true) return true;

  const session = await signInOrSignUp(message);
  if (session === "pending-verification") return false;
  state.session = session;
  renderAccount(session);
  renderGate();
  if (session.limits) await loadPro(session.limits);
  renderQueue();
  return true;
}

/**
 * Load the Pro chunk, but only for a session the server says is entitled.
 *
 * This is the licence control that actually works. The code for layer
 * remapping, naming templates, the report client and saved profiles is in a
 * chunk that a free account never downloads, so there is nothing in it to
 * extract. Every other control here can be edited out by someone determined
 * enough; this one cannot, because the bytes are not on their machine.
 */
async function loadPro(limits: Limits): Promise<void> {
  if (!limits.mayRemapLayers && !limits.mayGenerateReport) return;

  try {
    const pro = await import("./pro/index.ts");
    pro.install({ api, limits });
  } catch {
    // A failed chunk load must not break the free path.
  }
}

// --- chrome ----------------------------------------------------------------

function setBusy(text: string): void {
  busyText.textContent = text;
  busy.hidden = false;
}

function clearBusy(): void {
  busy.hidden = true;
}

function showFatal(message: string): void {
  panels.show();
  panels.renderWarnings([{ code: "parse", severity: "error", message }]);
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (found === null) throw new Error(`The page is missing #${id}.`);
  return found as T;
}

/**
 * True while the "signed in elsewhere" prompt is already open, so a burst of
 * requests that all 401 around the same moment (e.g. a fire-and-forget
 * `recordSplit` alongside an `entitlement` check) reopens the dialog once,
 * not once per request — `AuthDialog.open()` only tracks one pending call at
 * a time and a second concurrent call would clobber it.
 */
let handlingSessionSuperseded = false;

/**
 * Wired to `Api.onSessionSuperseded`, fired from any guarded call once the
 * server reports this browser's session was superseded by a newer login
 * elsewhere. Every guarded endpoint gets this for free from one place, not
 * just the export flow.
 */
async function handleSessionSuperseded(): Promise<void> {
  if (handlingSessionSuperseded) return;
  handlingSessionSuperseded = true;
  try {
    state.session = { signedIn: false };
    renderAccount(state.session);
    resetToEmpty();
    queue.length = 0;
    activeQueueIndex = -1;
    renderGate();
    renderQueue();
    await signInOrSignUpOrCancel("You've been logged out because your account was signed in elsewhere.");
  } finally {
    handlingSessionSuperseded = false;
  }
}
api.onSessionSuperseded = () => void handleSessionSuperseded();

viewport.resized();
void loadSession();
